import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Globe } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useBlogTranslation } from "../translations";
import axios from "axios";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_teams-integration/artifacts/bfkk1953_Cayus%20Square%20Logo%20%28150%20x%2050%20px%29.png";

const LANG_LABELS = { en: "English", fr: "Français", es: "Español" };

/** Clean HTML from rich-text editor artifacts */
const cleanHtml = (html) => {
  if (!html) return "";
  let clean = html;
  // Replace &nbsp; with regular spaces
  clean = clean.replace(/&nbsp;/g, " ");
  // Remove inline color styles
  clean = clean.replace(/\s*style="[^"]*"/g, "");
  // Unwrap empty spans
  clean = clean.replace(/<span\s*>(.*?)<\/span>/g, "$1");
  // Remove empty paragraphs
  clean = clean.replace(/<p>\s*<\/p>/g, "");
  return clean;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const { language: globalLang } = useLanguage();
  const [articleLang, setArticleLang] = useState(globalLang);
  const t = useBlogTranslation(articleLang);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/blog/articles/${slug}`)
      .then((res) => setArticle(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Available languages for this article (non-empty content)
  const availableLangs = article
    ? ["fr", "en", "es"].filter(
        (l) => article[`title_${l}`]?.trim() && article[`content_${l}`]?.trim()
      )
    : [];

  const getField = (field) => {
    if (!article) return "";
    return article[`${field}_${articleLang}`] || article[`${field}_fr`] || article[`${field}_en`] || "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(
      articleLang === "fr" ? "fr-FR" : articleLang === "es" ? "es-ES" : "en-GB",
      { year: "numeric", month: "long", day: "numeric" }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#D9D9D9] border-t-[#0047AB] rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4" data-testid="blog-post-not-found">
        <p className="text-xl text-[#5E6C83]">Article not found</p>
        <Link to="/blog" className="text-[#0047AB] hover:underline flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" />
          {t.backToBlog}
        </Link>
      </div>
    );
  }

  const title = getField("title");
  const content = cleanHtml(getField("content"));
  const imageUrl = article.image_url
    ? article.image_url.startsWith("/api")
      ? `${process.env.REACT_APP_BACKEND_URL}${article.image_url}`
      : article.image_url
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      <Navbar isScrolled={true} />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm text-[#9098A3] mb-6" data-testid="blog-post-date">
          <Calendar className="w-4 h-4" />
          {t.publishedOn} {formatDate(article.created_at)}
        </div>

        {/* Title */}
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1B1B1B] tracking-tight mb-8 leading-tight"
          data-testid="blog-post-title"
        >
          {title}
        </h1>

        {/* Cover image */}
        {imageUrl && (
          <div className="rounded-2xl overflow-hidden mb-10 border border-[#ECECEC]" data-testid="blog-post-cover">
            <img src={imageUrl} alt={title} className="w-full h-auto" onError={(e) => { e.target.onerror = null; e.target.parentElement.style.display = 'none'; }} />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-slate prose-lg max-w-none break-words
            prose-headings:font-bold prose-headings:tracking-tight
            prose-p:leading-relaxed prose-p:text-[#4D4D4D]
            prose-strong:text-[#1B1B1B]
            prose-a:text-[#0047AB] prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-xl prose-img:border prose-img:border-[#ECECEC]
            prose-li:text-[#4D4D4D]"
          style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
          dangerouslySetInnerHTML={{ __html: content }}
          data-testid="blog-post-content"
        />
      </article>

      {/* Footer */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="border-t border-[#D9D9D9] pt-8">
          <Link
            to="/blog"
            className="text-[#0047AB] hover:underline flex items-center gap-1.5 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToBlog}
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default BlogPostPage;
